import React, { useState, useEffect } from "react";
import toast from 'react-hot-toast';
import { obtenerVeterinariosDeUsuario } from "../services/api/modules/vets";
import { getAuthHeaders } from "../services/api";
import catalogo from '../data/catalogoMedicamentos.json';

export default function ExpedienteClinicoForm({
  initialData,
  onSave,
  onCancel,
  onDelete,
  onAnular,
  onCertificar,
  permisos,
  usuario,
  objetoId,
  canDeleteFiles = true,
  // Props para vacunas asociadas a esta cita
  vacunasAsociadas = [],
  onEditVacuna,
  onDeleteVacuna,
  // Perfil profesional del veterinario (cuando es acceso de vet)
  perfilVeterinario = null
}) {
  // Detectar si es acceso de veterinario (tiene perfilVeterinario)
  const esAccesoVeterinario = Boolean(perfilVeterinario);
  // Mapeo de tipos para compatibilidad
  const mapearTipoLegacy = (tipo) => {
    const mapeo = {
      'Consulta General': 'consulta',
      'Vacunación': 'vacunacion',
      'Desparasitación': 'desparasitacion',
      'Emergencia': 'emergencia',
      'Cirugía': 'cirugia',
      'Análisis/Estudios': 'analisis',
      'Control de Peso': 'control_peso',
      'Baño/Estética': 'estetica',
      'Otro': 'otro'
    };
    return mapeo[tipo] || tipo.toLowerCase().replace(/ /g, '_');
  };

  // Estado del formulario
  const [formData, setFormData] = useState({
    fecha: initialData?.fecha || new Date().toISOString().slice(0, 16),
    vetId: initialData?.vetId || "",
    veterinarioNombre: initialData?.veterinarioNombre || "",
    peso: initialData?.peso || "",
    temperatura: initialData?.temperatura || "",
    recetaUrl: initialData?.recetaUrl || initialData?.recetaPath || "",
    documentoAdicionalUrl: initialData?.documentoAdicionalUrl || initialData?.documentoAdicionalPath || "",
    notas: initialData?.notas || ""
  });

  // Servicios seleccionados (checkboxes)
  const [serviciosSeleccionados, setServiciosSeleccionados] = useState(() => {
    if (!initialData) return [];
    if (initialData.tipos) return initialData.tipos;
    if (initialData.tipo) return [mapearTipoLegacy(initialData.tipo)];
    return [];
  });

  // Datos de consulta
  const [consultaData, setConsultaData] = useState({
    observaciones: initialData?.consulta?.observaciones || initialData?.notas || ""
  });

  // Datos de vacunas (múltiples)
  const [vacunasData, setVacunasData] = useState(() => {
    if (initialData?.vacunas?.length > 0) return initialData.vacunas;
    if (initialData?.nombreVacuna) {
      return [{
        nombre: initialData.nombreVacuna,
        laboratorio: initialData.laboratorio || "",
        lote: initialData.numeroLote || "",
        caducidad: initialData.fechaCaducidad || ""
      }];
    }
    return [{ nombre: "", laboratorio: "", lote: "", caducidad: "" }];
  });

  // Datos de desparasitaciones (múltiples)
  const [desparasitacionesData, setDesparasitacionesData] = useState(() => {
    if (initialData?.desparasitaciones?.length > 0) return initialData.desparasitaciones;
    return [{ producto: "", dosis: "", tipo: "" }];
  });

  // Datos de emergencia
  const [emergenciaData, setEmergenciaData] = useState({
    descripcion: initialData?.emergencia?.descripcion || "",
    gravedad: initialData?.emergencia?.gravedad || "media"
  });

  // Datos de cirugía
  const [cirugiaData, setCirugiaData] = useState({
    procedimiento: initialData?.cirugia?.procedimiento || "",
    anestesia: initialData?.cirugia?.anestesia || "",
    observaciones: initialData?.cirugia?.observaciones || ""
  });

  // Estado para veterinarios del usuario
  const [veterinarios, setVeterinarios] = useState([]);
  const [cargandoVets, setCargandoVets] = useState(false);

  // Cargar veterinarios del usuario al montar el componente
  useEffect(() => {
    async function cargarVeterinarios() {
      if (!usuario?.ownerId) return;

      setCargandoVets(true);
      try {
        const resp = await obtenerVeterinariosDeUsuario(usuario.ownerId);
        if (resp?.data?.veterinarios) {
          setVeterinarios(resp.data.veterinarios);
        }
      } catch (error) {
        console.error("Error al cargar veterinarios:", error);
      } finally {
        setCargandoVets(false);
      }
    }

    cargarVeterinarios();
  }, [usuario?.ownerId]);

  const [recetaFile, setRecetaFile] = useState(null);
  const [documentoAdicionalFile, setDocumentoAdicionalFile] = useState(null);
  const [subiendoArchivos, setSubiendoArchivos] = useState(false);
  const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [fileToDelete, setFileToDelete] = useState(null);

  // Estado para errores de validación inline
  const [errores, setErrores] = useState({
    peso: '',
    temperatura: '',
    servicios: ''
  });

  // Tipos de servicios disponibles
  const tiposServicio = [
    { id: 'consulta', label: 'Consulta General', icon: '🩺', color: '#3b82f6' },
    { id: 'vacunacion', label: 'Vacunación', icon: '💉', color: '#10b981' },
    { id: 'desparasitacion', label: 'Desparasitación', icon: '💊', color: '#8b5cf6' },
    { id: 'emergencia', label: 'Emergencia', icon: '🚨', color: '#ef4444' },
    { id: 'cirugia', label: 'Cirugía', icon: '🏥', color: '#f59e0b' },
    { id: 'analisis', label: 'Análisis/Estudios', icon: '🔬', color: '#06b6d4' },
    { id: 'estetica', label: 'Baño/Estética', icon: '✨', color: '#ec4899' },
    { id: 'otro', label: 'Otro', icon: '📋', color: '#6b7280' }
  ];

  // Vacunas sugeridas (desde catálogo centralizado)
  const vacunasSugeridas = [...catalogo.vacunas.map(v => v.nombre), "Otra"];

  // Productos de desparasitación sugeridos (desde catálogo centralizado)
  const desparasitantesSugeridos = [...catalogo.desparasitantes.map(d => d.nombre), "Otro"];

  // Función de validación de campos
  const validarCampo = (campo, valor) => {
    switch (campo) {
      case 'peso':
        if (valor !== '' && valor !== null && valor !== undefined) {
          const peso = parseFloat(valor);
          if (isNaN(peso)) return 'El peso debe ser un número válido';
          if (peso < 0 || peso > 200) return 'El peso debe estar entre 0 y 200 kg';
        }
        break;
      case 'temperatura':
        if (valor !== '' && valor !== null && valor !== undefined) {
          const temp = parseFloat(valor);
          if (isNaN(temp)) return 'La temperatura debe ser un número válido';
          if (temp < 25 || temp > 45) return 'La temperatura debe estar entre 25°C y 45°C';
        }
        break;
      default:
        break;
    }
    return '';
  };

  // Toggle de servicio seleccionado
  const toggleServicio = (servicioId) => {
    setServiciosSeleccionados(prev => {
      if (prev.includes(servicioId)) {
        return prev.filter(s => s !== servicioId);
      } else {
        return [...prev, servicioId];
      }
    });
    // Limpiar error de servicios si se selecciona algo
    setErrores(prev => ({ ...prev, servicios: '' }));
  };

  // Handlers para vacunas
  const agregarVacuna = () => {
    setVacunasData(prev => [...prev, { nombre: "", laboratorio: "", lote: "", caducidad: "" }]);
  };

  const eliminarVacuna = (index) => {
    if (vacunasData.length > 1) {
      setVacunasData(prev => prev.filter((_, i) => i !== index));
    }
  };

  const actualizarVacuna = (index, campo, valor) => {
    setVacunasData(prev => prev.map((v, i) => i === index ? { ...v, [campo]: valor } : v));
  };

  // Handlers para desparasitaciones
  const agregarDesparasitacion = () => {
    setDesparasitacionesData(prev => [...prev, { producto: "", dosis: "", tipo: "" }]);
  };

  const eliminarDesparasitacion = (index) => {
    if (desparasitacionesData.length > 1) {
      setDesparasitacionesData(prev => prev.filter((_, i) => i !== index));
    }
  };

  const actualizarDesparasitacion = (index, campo, valor) => {
    setDesparasitacionesData(prev => prev.map((d, i) => i === index ? { ...d, [campo]: valor } : d));
  };

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }

  // Handler especial para el dropdown de veterinario
  function handleVetChange(e) {
    const value = e.target.value;

    if (value === "") {
      setFormData((prev) => ({ ...prev, vetId: "", veterinarioNombre: "" }));
    } else if (value === "__otro__") {
      setFormData((prev) => ({ ...prev, vetId: "__otro__", veterinarioNombre: prev.veterinarioNombre || "" }));
    } else {
      const vetSeleccionado = veterinarios.find(v => v.id === value);
      setFormData((prev) => ({
        ...prev,
        vetId: value,
        veterinarioNombre: vetSeleccionado?.nombre || ""
      }));
    }
  }

  function validarArchivo(file) {
    if (file.size > MAX_FILE_SIZE) {
      alert(`${file.name} excede 2MB (${(file.size / 1024 / 1024).toFixed(2)}MB)`);
      return false;
    }
    const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "application/pdf"];
    if (!allowedTypes.includes(file.type)) {
      alert(`${file.name}: Solo se permiten imágenes (JPG, PNG) y archivos PDF`);
      return false;
    }
    return true;
  }

  function handleRecetaChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (validarArchivo(file)) {
      setRecetaFile(file);
    } else {
      e.target.value = '';
    }
  }

  function handleDocumentoAdicionalChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (validarArchivo(file)) {
      setDocumentoAdicionalFile(file);
    } else {
      e.target.value = '';
    }
  }

  function removeReceta() {
    setRecetaFile(null);
    if (document.getElementById('recetaInput')) {
      document.getElementById('recetaInput').value = '';
    }
  }

  function removeDocumentoAdicional() {
    setDocumentoAdicionalFile(null);
    if (document.getElementById('documentoAdicionalInput')) {
      document.getElementById('documentoAdicionalInput').value = '';
    }
  }

  async function abrirDocumento(tipo) {
    const entryId = initialData?.id;

    if (!entryId || !objetoId) {
      const url = tipo === 'receta' ? formData.recetaUrl : formData.documentoAdicionalUrl;
      if (url && url.startsWith('http')) {
        window.open(url, '_blank');
      }
      return;
    }

    const campo = tipo === 'receta' ? 'receta' : 'documento';
    const proxyUrl = `/api/archivo/expediente/${objetoId}/${entryId}/${campo}`;

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(proxyUrl, { method: 'GET', headers });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: No se pudo cargar el archivo`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);

    } catch (error) {
      console.error('[abrirDocumento] Error:', error);
      toast.error('No se pudo cargar el archivo: ' + error.message);
    }
  }

  function quitarDocumentoGuardado(tipo) {
    setFileToDelete({ tipo, url: tipo === 'receta' ? formData.recetaUrl : formData.documentoAdicionalUrl });
    setShowDeleteModal(true);
    setDeleteConfirmText("");
  }

  function confirmarEliminacion() {
    if (deleteConfirmText.toLowerCase() === "borrar") {
      if (fileToDelete.tipo === 'receta') {
        setFormData({...formData, recetaUrl: ''});
      } else {
        setFormData({...formData, documentoAdicionalUrl: ''});
      }
      setShowDeleteModal(false);
      setFileToDelete(null);
      setDeleteConfirmText("");
    }
  }

  async function subirArchivo(file, tipoArchivo) {
    const formDataUpload = new FormData();
    formDataUpload.append("ownerId", usuario?.ownerId || "");
    formDataUpload.append("objetoId", objetoId);
    formDataUpload.append("foto", file);
    formDataUpload.append("tipo", "expediente");

    const resp = await fetch("/uploadFoto", {
      method: "POST",
      body: formDataUpload,
    });

    if (!resp.ok) throw new Error(`Error al subir ${tipoArchivo}`);
    const data = await resp.json();
    if (data.error) throw new Error(data.error);
    return data.downloadURL || data.url;
  }

  async function handleSubmit(e) {
    e.preventDefault();

    // Validar que al menos un servicio esté seleccionado
    if (serviciosSeleccionados.length === 0) {
      setErrores(prev => ({ ...prev, servicios: 'Debes seleccionar al menos un tipo de servicio' }));
      toast.error('Selecciona al menos un tipo de servicio');
      return;
    }

    // Validar campos numéricos
    const errorPeso = validarCampo('peso', formData.peso);
    const errorTemperatura = validarCampo('temperatura', formData.temperatura);

    setErrores(prev => ({
      ...prev,
      peso: errorPeso,
      temperatura: errorTemperatura
    }));

    if (errorPeso || errorTemperatura) return;

    try {
      let recetaUrl = formData.recetaUrl;
      let documentoAdicionalUrl = formData.documentoAdicionalUrl;

      if (recetaFile || documentoAdicionalFile) {
        setSubiendoArchivos(true);
        if (recetaFile) recetaUrl = await subirArchivo(recetaFile, "receta");
        if (documentoAdicionalFile) documentoAdicionalUrl = await subirArchivo(documentoAdicionalFile, "documento adicional");
        setSubiendoArchivos(false);
      }

      // Limpiar vetId si es "__otro__"
      const vetIdFinal = formData.vetId === "__otro__" ? null : (formData.vetId || null);

      // Construir objeto de datos final
      const finalData = {
        fecha: formData.fecha,
        vetId: vetIdFinal,
        veterinarioNombre: formData.veterinarioNombre,
        peso: formData.peso,
        temperatura: formData.temperatura,
        recetaUrl,
        documentoAdicionalUrl,

        // Nuevo formato: array de tipos
        tipos: serviciosSeleccionados,

        // Mantener campo legacy para compatibilidad (primer tipo seleccionado)
        tipo: serviciosSeleccionados.length > 0
          ? tiposServicio.find(t => t.id === serviciosSeleccionados[0])?.label || serviciosSeleccionados[0]
          : '',

        // Datos específicos por tipo
        ...(serviciosSeleccionados.includes('consulta') && {
          consulta: consultaData,
          notas: consultaData.observaciones // Mantener notas para compatibilidad
        }),

        ...(serviciosSeleccionados.includes('vacunacion') && {
          vacunas: vacunasData.filter(v => v.nombre.trim() !== ''),
          // Para compatibilidad legacy, guardar primera vacuna en campos planos
          nombreVacuna: vacunasData[0]?.nombre || '',
          laboratorio: vacunasData[0]?.laboratorio || '',
          numeroLote: vacunasData[0]?.lote || '',
          fechaCaducidad: vacunasData[0]?.caducidad || ''
        }),

        ...(serviciosSeleccionados.includes('desparasitacion') && {
          desparasitaciones: desparasitacionesData.filter(d => d.producto.trim() !== '')
        }),

        ...(serviciosSeleccionados.includes('emergencia') && {
          emergencia: emergenciaData
        }),

        ...(serviciosSeleccionados.includes('cirugia') && {
          cirugia: cirugiaData
        }),

        // Si no es consulta pero hay notas generales
        ...(!serviciosSeleccionados.includes('consulta') && formData.notas && {
          notas: formData.notas
        })
      };

      onSave?.(finalData);
    } catch (error) {
      setSubiendoArchivos(false);
      toast.error("Error al guardar: " + error.message);
    }
  }

  function getFileIcon(file) {
    if (file?.type === 'application/pdf') return 'bi-file-earmark-pdf text-danger';
    return 'bi-file-earmark-image';
  }

  const tieneErrores = () => errores.peso || errores.temperatura || errores.servicios;

  return (
    <div className="card card-elevated" style={{borderRadius: '12px'}}>
      <div style={{
        background: initialData?.anulado
          ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
          : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        color: '#fff',
        padding: '16px',
        borderRadius: '12px 12px 0 0'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h5 className="mb-0" style={{ fontSize: '18px', fontWeight: '600' }}>
            {initialData?.anulado ? "Registro Anulado" : initialData ? "Editar Visita Médica" : "Nueva Visita Médica"}
          </h5>
          {initialData && (
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              {initialData.certificado && !initialData.anulado && (
                <span style={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  padding: '3px 8px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '500'
                }}>
                  Certificado
                </span>
              )}
              {initialData.tipoAutor === 'veterinario' && (
                <span style={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  padding: '3px 8px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '500'
                }}>
                  Vet: {initialData.nombreAutor || 'Veterinario'}
                </span>
              )}
              {initialData.tipoAutor === 'propietario' && (
                <span style={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  padding: '3px 8px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '500'
                }}>
                  Por: Propietario
                </span>
              )}
            </div>
          )}
        </div>
        {initialData?.anulado && initialData.razonAnulacion && (
          <div style={{ marginTop: '8px', fontSize: '13px', opacity: 0.9 }}>
            Razón: {initialData.razonAnulacion}
          </div>
        )}
      </div>

      <div className="card-body" style={{padding: '20px'}}>
        {subiendoArchivos && (
          <div className="alert alert-info rounded-lg" style={{ fontSize: '14px', padding: '12px' }}>
            <span className="spinner-border spinner-border-sm me-2"></span>
            Subiendo archivos...
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Campos generales */}
          <div className="row g-3 mb-4">
            {/* Fecha */}
            <div className="col-md-6">
              <label className="form-label" style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                Fecha y Hora
              </label>
              <input
                type="datetime-local"
                className="form-control"
                name="fecha"
                value={formData.fecha}
                onChange={handleChange}
                required
                max={new Date().toISOString().slice(0, 16)}
                style={{ fontSize: '14px', borderRadius: '8px', padding: '10px' }}
              />
              <small style={{ color: '#6b7280', fontSize: '12px' }}>
                Solo se pueden registrar visitas pasadas o de hoy
              </small>
            </div>

            {/* Veterinario */}
            <div className="col-md-6">
              <label className="form-label" style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                Veterinario
              </label>
              {esAccesoVeterinario ? (
                /* Vista de solo lectura para veterinarios */
                <div style={{
                  backgroundColor: '#f0fdf4',
                  border: '1px solid #86efac',
                  borderRadius: '8px',
                  padding: '12px'
                }}>
                  <div style={{ fontWeight: '600', color: '#166534', fontSize: '14px' }}>
                    {perfilVeterinario.nombreCompleto || 'Veterinario'}
                  </div>
                  {perfilVeterinario.cedulaProfesional && (
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                      Cedula: {perfilVeterinario.cedulaProfesional}
                    </div>
                  )}
                  {perfilVeterinario.nombreClinica && (
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                      {perfilVeterinario.nombreClinica}
                    </div>
                  )}
                </div>
              ) : (
                /* Selector para propietarios */
                <>
                  <select
                    className="form-select"
                    name="vetId"
                    value={formData.vetId}
                    onChange={handleVetChange}
                    disabled={cargandoVets}
                    style={{ fontSize: '14px', borderRadius: '8px', padding: '10px' }}
                  >
                    <option value="">Sin veterinario</option>
                    {veterinarios.map(vet => (
                      <option key={vet.id} value={vet.id}>
                        {vet.nombre}{vet.especialidad ? ` - ${vet.especialidad}` : ""}
                      </option>
                    ))}
                    <option value="__otro__">Otro veterinario</option>
                  </select>
                  {formData.vetId === "__otro__" && (
                    <input
                      type="text"
                      className="form-control mt-2"
                      placeholder="Nombre del veterinario"
                      value={formData.veterinarioNombre}
                      onChange={(e) => setFormData(prev => ({ ...prev, veterinarioNombre: e.target.value }))}
                      style={{ fontSize: '14px', borderRadius: '8px', padding: '10px' }}
                    />
                  )}
                </>
              )}
            </div>

            {/* Peso */}
            <div className="col-6 col-md-3">
              <label className="form-label" style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                Peso (kg)
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="200"
                className={`form-control ${errores.peso ? 'is-invalid' : ''}`}
                name="peso"
                value={formData.peso}
                onChange={(e) => {
                  handleChange(e);
                  setErrores(prev => ({ ...prev, peso: validarCampo('peso', e.target.value) }));
                }}
                placeholder="Ej: 5.2"
                style={{ fontSize: '14px', borderRadius: '8px', padding: '10px' }}
              />
              {errores.peso && <div className="invalid-feedback">{errores.peso}</div>}
            </div>

            {/* Temperatura */}
            <div className="col-6 col-md-3">
              <label className="form-label" style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                Temp. (°C)
              </label>
              <input
                type="number"
                step="0.1"
                min="25"
                max="45"
                className={`form-control ${errores.temperatura ? 'is-invalid' : ''}`}
                name="temperatura"
                value={formData.temperatura}
                onChange={(e) => {
                  handleChange(e);
                  setErrores(prev => ({ ...prev, temperatura: validarCampo('temperatura', e.target.value) }));
                }}
                placeholder="38.5"
                style={{ fontSize: '14px', borderRadius: '8px', padding: '10px' }}
              />
              {errores.temperatura && <div className="invalid-feedback">{errores.temperatura}</div>}
            </div>
          </div>

          {/* Sección de servicios (checkboxes) */}
          <div style={{
            border: '1px solid #e5e7eb',
            borderRadius: '10px',
            padding: '16px',
            marginBottom: '20px',
            backgroundColor: errores.servicios ? '#fef2f2' : '#f9fafb'
          }}>
            <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px', display: 'block' }}>
              ¿Qué se realizó en esta visita? *
            </label>
            {errores.servicios && (
              <div style={{ color: '#dc2626', fontSize: '13px', marginBottom: '10px' }}>
                <i className="bi bi-exclamation-circle me-1"></i>
                {errores.servicios}
              </div>
            )}

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {tiposServicio.map(servicio => (
                <button
                  key={servicio.id}
                  type="button"
                  onClick={() => toggleServicio(servicio.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 14px',
                    borderRadius: '20px',
                    border: serviciosSeleccionados.includes(servicio.id)
                      ? `2px solid ${servicio.color}`
                      : '2px solid #e5e7eb',
                    backgroundColor: serviciosSeleccionados.includes(servicio.id)
                      ? `${servicio.color}15`
                      : '#fff',
                    color: serviciosSeleccionados.includes(servicio.id)
                      ? servicio.color
                      : '#6b7280',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <span>{servicio.icon}</span>
                  <span>{servicio.label}</span>
                  {serviciosSeleccionados.includes(servicio.id) && (
                    <i className="bi bi-check-lg" style={{ marginLeft: '2px' }}></i>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Campos dinámicos según servicios seleccionados */}

          {/* CONSULTA */}
          {serviciosSeleccionados.includes('consulta') && (
            <div style={{
              border: '1px solid #bfdbfe',
              borderRadius: '10px',
              padding: '16px',
              marginBottom: '16px',
              backgroundColor: '#eff6ff'
            }}>
              <h6 style={{ fontSize: '14px', fontWeight: '600', color: '#1e40af', marginBottom: '12px' }}>
                🩺 Consulta General
              </h6>
              <textarea
                className="form-control"
                rows="3"
                placeholder="Observaciones de la consulta, diagnóstico, recomendaciones..."
                value={consultaData.observaciones}
                onChange={(e) => setConsultaData({ observaciones: e.target.value })}
                style={{ fontSize: '14px', borderRadius: '8px' }}
              />
            </div>
          )}

          {/* VACUNACIÓN */}
          {serviciosSeleccionados.includes('vacunacion') && (
            <div style={{
              border: '1px solid #bbf7d0',
              borderRadius: '10px',
              padding: '16px',
              marginBottom: '16px',
              backgroundColor: '#f0fdf4'
            }}>
              <h6 style={{ fontSize: '14px', fontWeight: '600', color: '#166534', marginBottom: '12px' }}>
                💉 Vacunas aplicadas
              </h6>

              {vacunasData.map((vacuna, index) => (
                <div key={index} style={{
                  backgroundColor: '#fff',
                  border: '1px solid #dcfce7',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '10px'
                }}>
                  <div className="row g-2">
                    <div className="col-md-6">
                      <select
                        className="form-select form-select-sm"
                        value={vacuna.nombre}
                        onChange={(e) => actualizarVacuna(index, 'nombre', e.target.value)}
                        style={{ fontSize: '13px', borderRadius: '6px' }}
                      >
                        <option value="">Seleccionar vacuna...</option>
                        {vacunasSugeridas.map(v => (
                          <option key={v} value={v}>{v}</option>
                        ))}
                      </select>
                      {vacuna.nombre === 'Otra' && (
                        <input
                          type="text"
                          className="form-control form-control-sm mt-2"
                          placeholder="Nombre de la vacuna"
                          onChange={(e) => actualizarVacuna(index, 'nombre', e.target.value)}
                          style={{ fontSize: '13px', borderRadius: '6px' }}
                        />
                      )}
                    </div>
                    <div className="col-6 col-md-3">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Lote"
                        value={vacuna.lote}
                        onChange={(e) => actualizarVacuna(index, 'lote', e.target.value)}
                        style={{ fontSize: '13px', borderRadius: '6px' }}
                      />
                    </div>
                    <div className="col-6 col-md-3">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Laboratorio"
                        value={vacuna.laboratorio}
                        onChange={(e) => actualizarVacuna(index, 'laboratorio', e.target.value)}
                        style={{ fontSize: '13px', borderRadius: '6px' }}
                      />
                    </div>
                  </div>
                  {vacunasData.length > 1 && (
                    <button
                      type="button"
                      onClick={() => eliminarVacuna(index)}
                      className="btn btn-sm mt-2"
                      style={{ color: '#dc2626', fontSize: '12px', padding: '2px 8px' }}
                    >
                      <i className="bi bi-trash3 me-1"></i>Eliminar
                    </button>
                  )}
                </div>
              ))}

              <button
                type="button"
                onClick={agregarVacuna}
                className="btn btn-sm"
                style={{
                  backgroundColor: '#dcfce7',
                  color: '#166534',
                  border: 'none',
                  fontSize: '13px',
                  borderRadius: '6px'
                }}
              >
                <i className="bi bi-plus me-1"></i>Agregar otra vacuna
              </button>
            </div>
          )}

          {/* DESPARASITACIÓN */}
          {serviciosSeleccionados.includes('desparasitacion') && (
            <div style={{
              border: '1px solid #ddd6fe',
              borderRadius: '10px',
              padding: '16px',
              marginBottom: '16px',
              backgroundColor: '#f5f3ff'
            }}>
              <h6 style={{ fontSize: '14px', fontWeight: '600', color: '#5b21b6', marginBottom: '12px' }}>
                💊 Desparasitación
              </h6>

              {desparasitacionesData.map((desp, index) => (
                <div key={index} style={{
                  backgroundColor: '#fff',
                  border: '1px solid #e9d5ff',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '10px'
                }}>
                  <div className="row g-2">
                    <div className="col-md-6">
                      <select
                        className="form-select form-select-sm"
                        value={desp.producto}
                        onChange={(e) => actualizarDesparasitacion(index, 'producto', e.target.value)}
                        style={{ fontSize: '13px', borderRadius: '6px' }}
                      >
                        <option value="">Seleccionar producto...</option>
                        {desparasitantesSugeridos.map(p => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-3">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Dosis"
                        value={desp.dosis}
                        onChange={(e) => actualizarDesparasitacion(index, 'dosis', e.target.value)}
                        style={{ fontSize: '13px', borderRadius: '6px' }}
                      />
                    </div>
                    <div className="col-md-3">
                      <select
                        className="form-select form-select-sm"
                        value={desp.tipo}
                        onChange={(e) => actualizarDesparasitacion(index, 'tipo', e.target.value)}
                        style={{ fontSize: '13px', borderRadius: '6px' }}
                      >
                        <option value="">Tipo...</option>
                        <option value="interna">Interna</option>
                        <option value="externa">Externa</option>
                        <option value="ambas">Ambas</option>
                      </select>
                    </div>
                  </div>
                  {desparasitacionesData.length > 1 && (
                    <button
                      type="button"
                      onClick={() => eliminarDesparasitacion(index)}
                      className="btn btn-sm mt-2"
                      style={{ color: '#dc2626', fontSize: '12px', padding: '2px 8px' }}
                    >
                      <i className="bi bi-trash3 me-1"></i>Eliminar
                    </button>
                  )}
                </div>
              ))}

              <button
                type="button"
                onClick={agregarDesparasitacion}
                className="btn btn-sm"
                style={{
                  backgroundColor: '#e9d5ff',
                  color: '#5b21b6',
                  border: 'none',
                  fontSize: '13px',
                  borderRadius: '6px'
                }}
              >
                <i className="bi bi-plus me-1"></i>Agregar otra desparasitación
              </button>
            </div>
          )}

          {/* EMERGENCIA */}
          {serviciosSeleccionados.includes('emergencia') && (
            <div style={{
              border: '1px solid #fecaca',
              borderRadius: '10px',
              padding: '16px',
              marginBottom: '16px',
              backgroundColor: '#fef2f2'
            }}>
              <h6 style={{ fontSize: '14px', fontWeight: '600', color: '#991b1b', marginBottom: '12px' }}>
                🚨 Emergencia
              </h6>
              <div className="row g-2">
                <div className="col-md-3">
                  <label style={{ fontSize: '12px', color: '#6b7280' }}>Gravedad</label>
                  <select
                    className="form-select form-select-sm"
                    value={emergenciaData.gravedad}
                    onChange={(e) => setEmergenciaData(prev => ({ ...prev, gravedad: e.target.value }))}
                    style={{ fontSize: '13px', borderRadius: '6px' }}
                  >
                    <option value="leve">Leve</option>
                    <option value="media">Media</option>
                    <option value="grave">Grave</option>
                    <option value="critica">Crítica</option>
                  </select>
                </div>
                <div className="col-md-9">
                  <label style={{ fontSize: '12px', color: '#6b7280' }}>Descripción</label>
                  <textarea
                    className="form-control form-control-sm"
                    rows="2"
                    placeholder="Descripción de la emergencia..."
                    value={emergenciaData.descripcion}
                    onChange={(e) => setEmergenciaData(prev => ({ ...prev, descripcion: e.target.value }))}
                    style={{ fontSize: '13px', borderRadius: '6px' }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* CIRUGÍA */}
          {serviciosSeleccionados.includes('cirugia') && (
            <div style={{
              border: '1px solid #fcd34d',
              borderRadius: '10px',
              padding: '16px',
              marginBottom: '16px',
              backgroundColor: '#fefce8'
            }}>
              <h6 style={{ fontSize: '14px', fontWeight: '600', color: '#92400e', marginBottom: '12px' }}>
                🏥 Cirugía
              </h6>
              <div className="row g-2">
                <div className="col-md-6">
                  <label style={{ fontSize: '12px', color: '#6b7280' }}>Procedimiento</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Nombre del procedimiento"
                    value={cirugiaData.procedimiento}
                    onChange={(e) => setCirugiaData(prev => ({ ...prev, procedimiento: e.target.value }))}
                    style={{ fontSize: '13px', borderRadius: '6px' }}
                  />
                </div>
                <div className="col-md-6">
                  <label style={{ fontSize: '12px', color: '#6b7280' }}>Tipo de anestesia</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Ej: General, Local"
                    value={cirugiaData.anestesia}
                    onChange={(e) => setCirugiaData(prev => ({ ...prev, anestesia: e.target.value }))}
                    style={{ fontSize: '13px', borderRadius: '6px' }}
                  />
                </div>
                <div className="col-12">
                  <label style={{ fontSize: '12px', color: '#6b7280' }}>Observaciones</label>
                  <textarea
                    className="form-control form-control-sm"
                    rows="2"
                    placeholder="Observaciones de la cirugía..."
                    value={cirugiaData.observaciones}
                    onChange={(e) => setCirugiaData(prev => ({ ...prev, observaciones: e.target.value }))}
                    style={{ fontSize: '13px', borderRadius: '6px' }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Notas generales (si no hay consulta seleccionada) */}
          {!serviciosSeleccionados.includes('consulta') && serviciosSeleccionados.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <label className="form-label" style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                Notas adicionales (opcional)
              </label>
              <textarea
                className="form-control"
                rows="2"
                name="notas"
                value={formData.notas}
                onChange={handleChange}
                placeholder="Observaciones generales..."
                style={{ fontSize: '14px', borderRadius: '8px' }}
              />
            </div>
          )}

          {/* Sección de documentos */}
          <div style={{
            border: '1px solid #e5e7eb',
            borderRadius: '10px',
            padding: '16px',
            marginBottom: '20px',
            backgroundColor: '#f9fafb'
          }}>
            <h6 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
              📎 Documentos (opcional)
            </h6>

            <div className="row">
              {/* Receta */}
              <div className="col-md-6 mb-3">
                <label style={{ fontSize: '13px', fontWeight: '500', color: '#6b7280' }}>
                  Receta médica
                </label>
                {formData.recetaUrl && !recetaFile ? (
                  <div className="alert alert-info p-2 mb-2" style={{ fontSize: '13px' }}>
                    <div className="d-flex justify-content-between align-items-center">
                      <span>📄 Receta guardada</span>
                      <div>
                        <button type="button" className="btn btn-sm btn-primary me-1" onClick={() => abrirDocumento('receta')}>
                          Ver
                        </button>
                        {canDeleteFiles && (
                          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => quitarDocumentoGuardado('receta')}>
                            Borrar
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ) : !recetaFile ? (
                  <input
                    type="file"
                    id="recetaInput"
                    className="form-control form-control-sm"
                    accept="image/png,image/jpeg,image/jpg,application/pdf"
                    onChange={handleRecetaChange}
                    style={{ fontSize: '13px', borderRadius: '6px' }}
                  />
                ) : (
                  <div className="alert alert-success p-2" style={{ fontSize: '13px' }}>
                    <div className="d-flex justify-content-between align-items-center">
                      <span><i className={`bi ${getFileIcon(recetaFile)} me-1`}></i>{recetaFile.name}</span>
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={removeReceta}>
                        Quitar
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Documento adicional */}
              <div className="col-md-6 mb-3">
                <label style={{ fontSize: '13px', fontWeight: '500', color: '#6b7280' }}>
                  Documento adicional
                </label>
                {formData.documentoAdicionalUrl && !documentoAdicionalFile ? (
                  <div className="alert alert-info p-2 mb-2" style={{ fontSize: '13px' }}>
                    <div className="d-flex justify-content-between align-items-center">
                      <span>📄 Documento guardado</span>
                      <div>
                        <button type="button" className="btn btn-sm btn-primary me-1" onClick={() => abrirDocumento('documento')}>
                          Ver
                        </button>
                        {canDeleteFiles && (
                          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => quitarDocumentoGuardado('documento')}>
                            Cambiar
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ) : !documentoAdicionalFile ? (
                  <input
                    type="file"
                    id="documentoAdicionalInput"
                    className="form-control form-control-sm"
                    accept="image/png,image/jpeg,image/jpg,application/pdf"
                    onChange={handleDocumentoAdicionalChange}
                    style={{ fontSize: '13px', borderRadius: '6px' }}
                  />
                ) : (
                  <div className="alert alert-success p-2" style={{ fontSize: '13px' }}>
                    <div className="d-flex justify-content-between align-items-center">
                      <span><i className={`bi ${getFileIcon(documentoAdicionalFile)} me-1`}></i>{documentoAdicionalFile.name}</span>
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={removeDocumentoAdicional}>
                        Quitar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Medicamentos existentes (solo lectura) */}
          {initialData?.medicamentos?.length > 0 && (
            <div style={{
              border: '1px solid #e9d5ff',
              borderRadius: '10px',
              padding: '16px',
              marginBottom: '16px',
              backgroundColor: '#faf5ff'
            }}>
              <h6 style={{ fontSize: '14px', fontWeight: '600', color: '#7c3aed', marginBottom: '12px' }}>
                💊 Medicamentos recetados
              </h6>
              {initialData.medicamentos.map((med, idx) => (
                <div key={idx} style={{
                  backgroundColor: '#fff',
                  border: '1px solid #e9d5ff',
                  borderRadius: '8px',
                  padding: '10px',
                  marginBottom: '8px'
                }}>
                  <div style={{ fontWeight: '600', color: '#1f2937' }}>{med.nombre}</div>
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>
                    {med.dosis && <span>Dosis: {med.dosis}</span>}
                    {med.frecuenciaHoras && <span> • Cada {med.frecuenciaHoras}h</span>}
                    {med.duracionDias && <span> • {med.duracionDias} días</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Vacunas asociadas (solo lectura) */}
          {vacunasAsociadas.length > 0 && (
            <div style={{
              border: '1px solid #bbf7d0',
              borderRadius: '10px',
              padding: '16px',
              marginBottom: '16px',
              backgroundColor: '#f0fdf4'
            }}>
              <h6 style={{ fontSize: '14px', fontWeight: '600', color: '#166534', marginBottom: '12px' }}>
                💉 Vacunas registradas en esta visita ({vacunasAsociadas.length})
              </h6>
              {vacunasAsociadas.map((vacuna, idx) => (
                <div key={vacuna.id || idx} style={{
                  backgroundColor: '#fff',
                  border: '1px solid #bbf7d0',
                  borderRadius: '8px',
                  padding: '10px',
                  marginBottom: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <div style={{ fontWeight: '600', color: '#1f2937' }}>{vacuna.nombre}</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      {vacuna.laboratorio && `Lab: ${vacuna.laboratorio}`}
                      {vacuna.lote && ` • Lote: ${vacuna.lote}`}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {onEditVacuna && (
                      <button type="button" className="btn btn-sm" onClick={() => onEditVacuna(vacuna)}
                        style={{ padding: '4px 8px', fontSize: '12px', backgroundColor: '#f3f4f6', border: 'none' }}>
                        <i className="bi bi-pencil"></i>
                      </button>
                    )}
                    {onDeleteVacuna && (
                      <button type="button" className="btn btn-sm" onClick={() => onDeleteVacuna(vacuna.id)}
                        style={{ padding: '4px 8px', fontSize: '12px', color: '#dc2626', backgroundColor: '#fef2f2', border: 'none' }}>
                        <i className="bi bi-trash3"></i>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Alerta de errores */}
          {tieneErrores() && (
            <div className="alert alert-danger" style={{ fontSize: '13px', padding: '10px', borderRadius: '8px' }}>
              <i className="bi bi-exclamation-triangle me-2"></i>
              Corrige los errores antes de guardar
            </div>
          )}

          {/* Botones de acción */}
          {/* Si el registro está anulado, solo mostrar botón de cerrar */}
          {initialData?.anulado ? (
            <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onCancel}
                style={{
                  flex: 1,
                  fontSize: '14px',
                  padding: '12px',
                  borderRadius: '8px'
                }}
              >
                Cerrar
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '10px', marginTop: '16px', flexWrap: 'wrap' }}>
              {/* Botón de guardar (solo si puede editar) */}
              {(!initialData || permisos?.editar !== false) && (
                <button
                  type="submit"
                  className="btn"
                  disabled={subiendoArchivos || tieneErrores()}
                  style={{
                    flex: 1,
                    minWidth: '120px',
                    backgroundColor: '#1f2937',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: '600',
                    padding: '12px',
                    borderRadius: '8px',
                    border: 'none',
                    opacity: tieneErrores() ? 0.6 : 1
                  }}
                >
                  {subiendoArchivos ? "Guardando..." : "Guardar Visita"}
                </button>
              )}

              {/* Botón de certificar (solo veterinarios, para registros de propietarios) */}
              {initialData && onCertificar && (
                <button
                  type="button"
                  className="btn"
                  onClick={onCertificar}
                  disabled={subiendoArchivos}
                  style={{
                    fontSize: '14px',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    backgroundColor: '#059669',
                    color: '#fff',
                    border: 'none'
                  }}
                  title="Certificar este registro como válido"
                >
                  <i className="bi bi-check-circle me-1"></i>
                  Certificar
                </button>
              )}

              {/* Botón de anular */}
              {initialData && onAnular && (
                <button
                  type="button"
                  className="btn"
                  onClick={onAnular}
                  disabled={subiendoArchivos}
                  style={{
                    fontSize: '14px',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    backgroundColor: '#f59e0b',
                    color: '#fff',
                    border: 'none'
                  }}
                  title="Anular este registro (no se eliminará)"
                >
                  <i className="bi bi-x-circle me-1"></i>
                  Anular
                </button>
              )}

              {/* Botón de eliminar */}
              {initialData && onDelete && (
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={onDelete}
                  disabled={subiendoArchivos}
                  style={{ fontSize: '14px', padding: '12px 16px', borderRadius: '8px' }}
                  title="Eliminar permanentemente"
                >
                  <i className="bi bi-trash"></i>
                </button>
              )}

              <button
                type="button"
                className="btn btn-secondary"
                onClick={onCancel}
                disabled={subiendoArchivos}
                style={{ fontSize: '14px', padding: '12px 16px', borderRadius: '8px' }}
              >
                Cancelar
              </button>
            </div>
          )}
        </form>
      </div>

      {/* Modal de confirmación de eliminación de archivo */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          zIndex: 999999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            maxWidth: '400px',
            width: '100%',
            overflow: 'hidden'
          }}>
            <div style={{ backgroundColor: '#dc2626', color: '#fff', padding: '16px' }}>
              <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                <i className="bi bi-exclamation-triangle me-2"></i>
                Confirmar eliminación
              </h5>
            </div>
            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '14px', color: '#374151', marginBottom: '16px' }}>
                Esta acción es permanente. Escribe <strong style={{ color: '#dc2626' }}>borrar</strong> para confirmar:
              </p>
              <input
                type="text"
                className="form-control"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Escribe 'borrar'"
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && deleteConfirmText.toLowerCase() === 'borrar') {
                    confirmarEliminacion();
                  }
                }}
                style={{ fontSize: '14px', borderRadius: '8px', marginBottom: '16px' }}
              />
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(""); }}
                  style={{ fontSize: '14px', borderRadius: '8px' }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmarEliminacion}
                  disabled={deleteConfirmText.toLowerCase() !== "borrar"}
                  style={{ fontSize: '14px', borderRadius: '8px' }}
                >
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
